'use client'

import { Event, Participant } from '@/types'
import { Badge, Button, Card, Modal } from 'flowbite-react'
import { useState } from 'react'
import { Discord } from 'flowbite-react-icons/solid'
import { get, isEmpty, map, snakeCase, startCase } from 'lodash'
import { Rocket } from 'flowbite-react-icons/solid'
import { CAREER_LEVEL, TEAM_STATUS } from '@/types/Participant'
import { FaTelegram } from 'react-icons/fa'
import Image from 'next/image'
import Slack from '@/public/images/icons/slack.svg'
import ParticipantProfile from './ParticipantProfile'
import SocialNetwork from './SocialNetwork'
import DOMPurify from 'dompurify'
import { useUserInfo } from '@/app/web/context/UserInfoContext'

const ParticipantCard = ({
  participant,
  loginEmail,
  loginUsername,
  event,
}: {
  participant: Participant
  loginEmail?: string
  loginUsername?: string
  event: Event
}) => {
  const [openModal, setOpenModal] = useState(false)
  const { userInfo } = useUserInfo()

  // Check if user is admin
  const isAdmin = userInfo?.roles?.includes('admin') || false

  const showRegistration =
    Boolean(participant.registration_forms) &&
    (loginUsername === participant.username || isAdmin)

  // Remove once verified
  // console.log('[ParticipantCard] debug', {
  //   loginUsername,
  //   participantUsername: participant.username,
  //   equal: loginUsername === participant.username,
  //   isAdmin,
  //   userRoles: userInfo?.roles,
  //   hasRegistrationForms: Boolean(participant.registration_forms),
  //   showRegistration,
  //   types: {
  //     loginUsername: typeof loginUsername,
  //     participantUsername: typeof participant.username,
  //   },
  // })

  return (
    <section>
      <Card
        className="max-w-full shadow-none hover:shadow-md border-gray-100 hover:border-gray-400 min-h-[355px]"
        onClick={() => setOpenModal(true)}
      >
        <div className="flex flex-col divide-y">
          <div className="flex flex-col gap-4 pb-6">
            <div className="flex flex-wrap cursor-pointer">
              <ParticipantProfile
                participant={participant}
                email={loginEmail}
              />
            </div>
            {event.customized && (
              <div className="inline-flex items-stretch gap-3">
                <div
                  className={`w-fit px-2.5 py-0.5 ${
                    ['solo', 'team_completed'].includes(participant.team_status)
                      ? 'bg-gray-100 text-gray-900'
                      : 'bg-sky-100 text-blue-800'
                  } rounded-md text-xs`}
                >
                  {get(TEAM_STATUS, participant.team_status)}
                </div>
                <SocialNetwork socialNetwork={participant.social_network} />
              </div>
            )}
          </div>
          <div className="flex flex-col gap-4 pt-6">
            <div className="flex flex-col gap-2">
              <span className="text-gray-600 text-xs">
                Roles/Expertise Areas
              </span>
              <div className="text-xs text-gray-900">
                {isEmpty(participant.expertise_areas)
                  ? '-'
                  : participant.expertise_areas.join(' / ')}
              </div>
            </div>
            <div className="flex flex-col gap-2">
              <span className="text-gray-600 text-xs">Tech Stack</span>
              <div className="inline-flex flex-wrap gap-2">
                {isEmpty(participant.tech_stack)
                  ? '-'
                  : map(participant.tech_stack.slice(0, 6), (tech, index) => (
                      <Badge key={`${snakeCase(tech)}_${index}`} color="gray">
                        {tech}
                      </Badge>
                    ))}
              </div>
            </div>
          </div>
        </div>
      </Card>
      <Modal show={openModal} onClose={() => setOpenModal(false)}>
        <Modal.Header>
          <div className="flex flex-col">
            <div className="flex flex-wrap mb-4">
              <ParticipantProfile
                participant={participant}
                email={loginEmail}
              />
            </div>
            <div className="inline-flex items-stretch gap-3 mb-2">
              <div className="w-fit px-2.5 py-0.5 bg-sky-100 rounded-md text-blue-800 text-xs">
                {get(TEAM_STATUS, participant.team_status)}
              </div>
              <SocialNetwork socialNetwork={participant.social_network} />
            </div>
            <div className="inline-flex text-gray-500 text-xs">
              <Rocket className="h-4" />
              <span>Theme: {participant.challenge_theme.theme}</span>
            </div>
          </div>
        </Modal.Header>
        <Modal.Body>
          <div className="flex flex-col gap-6">
            <div className="grid grid-cols-2 gap-2">
              <div className="flex flex-col gap-2">
                <span className="text-gray-600 text-xs">
                  {startCase(participant.social_network)} Username
                </span>
                <span className="text-gray-900 text-xs">
                  {participant.social_network_username}
                </span>
              </div>
              <div className="flex flex-col gap-2">
                <span className="text-gray-600 text-xs">Current Profile</span>
                <span className="text-gray-900 text-xs">
                  {get(CAREER_LEVEL, participant.career_level)}
                </span>
              </div>
            </div>

            <div className="grid grid-cols-2 gap-2">
              <div className="flex flex-col gap-2">
                <span className="text-gray-600 text-xs">
                  Roles/Expertise Areas
                </span>
                <div className="text-xs text-gray-900">
                  {isEmpty(participant.expertise_areas)
                    ? '-'
                    : participant.expertise_areas.join(' / ')}
                </div>
              </div>
              <div className="flex flex-col gap-2">
                <span className="text-gray-600 text-xs">Tech Stack</span>
                <div className="inline-flex flex-wrap gap-2">
                  {isEmpty(participant.tech_stack)
                    ? '-'
                    : map(participant.tech_stack, (tech, index) => (
                        <Badge key={`${snakeCase(tech)}_${index}`} color="gray">
                          {tech}
                        </Badge>
                      ))}
                </div>
              </div>
            </div>

            {/* User responses for customized hackathon */}
            {showRegistration && (
              <>
                <hr />
                <div className="overflow-y-auto h-48">
                  <h3 className="font-semibold text-gray-600 text-md">
                    Registration Form
                  </h3>
                  {participant.registration_forms.map((form) => (
                    <div
                      key={`registration-form-${form.id}`}
                      className="flex flex-col gap-2 pt-4 first:pt-0"
                    >
                      <h3 className="text-gray-600 text-xs font-semibold">
                        {form.name}
                      </h3>
                      {form.registration_form_fields.map((field) => (
                        <div
                          key={field.id}
                          className="flex flex-col gap-2 pl-2"
                        >
                          <span className="text-gray-500 text-xs">
                            {field.name}:
                            <span
                              className="text-gray-900 text-xs pl-2"
                              dangerouslySetInnerHTML={{
                                __html: DOMPurify.sanitize(
                                  field.registration_response
                                ),
                              }}
                            ></span>
                          </span>
                        </div>
                      ))}
                    </div>
                  ))}
                </div>
              </>
            )}
          </div>
        </Modal.Body>
        <Modal.Footer>
          {participant.social_network === 'discord' && (
            <Button
              target="_blank"
              color="dark"
              href="https://discord.com/channels/@me"
            >
              <div className="inline-flex items-center gap-2">
                <Discord /> Go to Discord
              </div>
            </Button>
          )}
          {participant.social_network === 'slack' && (
            <Button target="_blank" color="dark" href="https://slack.com/">
              <div className="inline-flex items-center gap-2">
                <Image src={Slack} alt="Slack" /> Go to Slack
              </div>
            </Button>
          )}
          {participant.social_network === 'telegram' && (
            <Button
              target="_blank"
              color="dark"
              href="https://web.telegram.org/"
            >
              <div className="inline-flex items-center gap-2">
                <FaTelegram /> Go to Telegram
              </div>
            </Button>
          )}
        </Modal.Footer>
      </Modal>
    </section>
  )
}

export default ParticipantCard
